import { useIsMutating, useMutation, useMutationState, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiRequestError } from '@/lib/httpClient'
import { useAuthorization } from '@/contexts/AuthorizationContext'
import { useAuthSession } from '@/lib/session'
import { permissions } from '@/types/authorization'
import { queryKeys } from '@/api/queryKeys'
import {
  answerMcpAuthorization,
  fetchMcpAuthorization,
  type McpAuthorizationDecision,
} from '@/api/mcpAuthorize'
import {
  fetchMcpAccess,
  fetchMcpCanManage,
  fetchMcpConnections,
  fetchMyMcpAccess,
  fetchSupportMcpConnections,
  grantMcpAccess,
  removeMcpAccess,
  revokeMcpConnection,
} from '@/api/mcpAccess'
import {
  deleteMcpPlaybook,
  fetchMcpCatalog,
  fetchMcpCatalogValues,
  fetchMcpPlaybook,
  fetchMcpPlaybooks,
  saveMcpPlaybook,
} from '@/api/mcpPlaybooks'
import { fetchMcpFirmProfile, saveMcpFirmProfile } from '@/api/mcpFirmProfile'
import type { McpFirmProfile, McpSavePlaybookRequest } from '@/types/mcp'

// A 404 or 403 will not change on a retry; a network blip might.
const noRetryOnClientError = (failureCount: number, error: unknown) =>
  !(error instanceof ApiRequestError && error.status >= 400 && error.status < 500) && failureCount < 2

export const isForbidden = (error: unknown) => error instanceof ApiRequestError && error.status === 403

const grantAccessKey = (clientId: string) => ['mcp', 'grantAccess', clientId] as const
const removeAccessKey = (clientId: string) => ['mcp', 'removeAccess', clientId] as const
const savePlaybookKey = (clientId: string) => ['mcp', 'savePlaybook', clientId] as const

const useCurrentUserId = () => useAuthSession()?.currentUserId ?? ''

export function useMcpAuthorization(requestId: string) {
  const userId = useCurrentUserId()
  return useQuery({
    queryKey: queryKeys.mcp.authorization(requestId, userId),
    queryFn: () => fetchMcpAuthorization(requestId),
    enabled: Boolean(requestId),
    retry: noRetryOnClientError,
    refetchOnWindowFocus: false,
  })
}

export function useAnswerMcpAuthorizationMutation(requestId: string) {
  return useMutation({
    mutationFn: ({ decision, clientId }: { decision: McpAuthorizationDecision; clientId?: string }) =>
      answerMcpAuthorization(requestId, decision, clientId),
  })
}

export type McpManageStatus = 'loading' | 'allowed' | 'denied' | 'error'

/** The role is broader than who the API lets manage (Owners, a Restricted client's admins, advisors not linked to it). */
export function useCanManageMcp(clientId: string) {
  const userId = useCurrentUserId()
  const hasRole = useAuthorization().hasPermission(permissions.manageClaudeAccess)
  const enabled = hasRole && Boolean(clientId && userId)
  const query = useQuery({
    queryKey: queryKeys.mcp.canManage(clientId, userId),
    queryFn: () => fetchMcpCanManage(clientId),
    enabled,
    retry: noRetryOnClientError,
    staleTime: 5 * 60 * 1000,
  })
  const status: McpManageStatus = !enabled
    ? 'denied'
    : query.data !== undefined
      ? query.data
        ? 'allowed'
        : 'denied'
      : query.isError
        ? 'error'
        : 'loading'
  return { status, canManage: status === 'allowed', error: query.error, retry: () => void query.refetch() }
}

export function useMcpAccess(clientId: string, enabled = true) {
  const userId = useCurrentUserId()
  return useQuery({
    queryKey: queryKeys.mcp.access(clientId, userId),
    queryFn: () => fetchMcpAccess(clientId),
    enabled: enabled && Boolean(clientId),
    retry: noRetryOnClientError,
  })
}

export function useMcpConnections(clientId: string) {
  const userId = useCurrentUserId()
  return useQuery({
    queryKey: queryKeys.mcp.connections(clientId, userId),
    queryFn: () => fetchMcpConnections(clientId),
    enabled: Boolean(clientId),
    retry: noRetryOnClientError,
  })
}

export function useSupportMcpConnections(enabled: boolean) {
  const userId = useCurrentUserId()
  return useQuery({
    queryKey: queryKeys.mcp.supportConnections(userId),
    queryFn: fetchSupportMcpConnections,
    enabled,
    retry: noRetryOnClientError,
  })
}

export function useMyMcpAccess() {
  const userId = useCurrentUserId()
  return useQuery({
    queryKey: queryKeys.mcp.myAccess(userId),
    queryFn: fetchMyMcpAccess,
    enabled: Boolean(userId),
    retry: noRetryOnClientError,
  })
}

/** Access and connections move together: removing access ends connections, and each list shows the other's counts. */
function useInvalidateMcpAccess() {
  const queryClient = useQueryClient()
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.mcp.accessAll }),
      queryClient.invalidateQueries({ queryKey: queryKeys.mcp.connectionsAll }),
      queryClient.invalidateQueries({ queryKey: queryKeys.mcp.myAccessAll }),
    ])
}

export function useGrantMcpAccessMutation(clientId: string) {
  const invalidate = useInvalidateMcpAccess()
  return useMutation({
    mutationKey: grantAccessKey(clientId),
    mutationFn: (userIds: string[]) => grantMcpAccess(clientId, userIds),
    onSettled: invalidate,
  })
}

export function useRemoveMcpAccessMutation(clientId: string) {
  const invalidate = useInvalidateMcpAccess()
  return useMutation({
    mutationKey: removeAccessKey(clientId),
    mutationFn: (userId: string) => removeMcpAccess(clientId, userId),
    onSettled: invalidate,
  })
}

const isString = (value: unknown): value is string => typeof value === 'string'

/** Everyone with a grant or removal in flight, across every call, not just the latest. */
export function usePendingMcpAccessUserIds(clientId: string): Set<string> {
  const grants = useMutationState({
    filters: { mutationKey: grantAccessKey(clientId), status: 'pending' },
    select: (mutation) => mutation.state.variables,
  })
  const removals = useMutationState({
    filters: { mutationKey: removeAccessKey(clientId), status: 'pending' },
    select: (mutation) => mutation.state.variables,
  })
  return new Set([...grants.flatMap((userIds) => (Array.isArray(userIds) ? userIds.filter(isString) : [])), ...removals.filter(isString)])
}

export function useRevokeMcpConnectionMutation() {
  const invalidate = useInvalidateMcpAccess()
  return useMutation({
    mutationFn: (connectionId: string) => revokeMcpConnection(connectionId),
    onSettled: invalidate,
  })
}

export function useMcpPlaybooks(clientId: string) {
  const userId = useCurrentUserId()
  return useQuery({
    queryKey: queryKeys.mcp.playbookList(clientId, userId),
    queryFn: () => fetchMcpPlaybooks(clientId),
    enabled: Boolean(clientId),
    retry: noRetryOnClientError,
  })
}

export function useMcpPlaybook(clientId: string, id: string | null) {
  const userId = useCurrentUserId()
  return useQuery({
    queryKey: queryKeys.mcp.playbook(clientId, id ?? '', userId),
    queryFn: () => fetchMcpPlaybook(clientId, id ?? ''),
    enabled: Boolean(clientId && id),
    retry: noRetryOnClientError,
    // Always refetched on open: the form saves the whole playbook, so editing a cached copy could undo newer edits.
    staleTime: 0,
  })
}

function useInvalidatePlaybooks(clientId: string) {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.mcp.playbooks(clientId) })
}

export function useSaveMcpPlaybookMutation(clientId: string) {
  const invalidate = useInvalidatePlaybooks(clientId)
  return useMutation({
    mutationKey: savePlaybookKey(clientId),
    mutationFn: (request: Omit<McpSavePlaybookRequest, 'clientId'>) => saveMcpPlaybook({ ...request, clientId }),
    onSuccess: invalidate,
  })
}

export function useIsSavingMcpPlaybook(clientId: string) {
  return useIsMutating({ mutationKey: savePlaybookKey(clientId) }) > 0
}

export function useDeleteMcpPlaybookMutation(clientId: string) {
  const invalidate = useInvalidatePlaybooks(clientId)
  return useMutation({
    mutationFn: (id: string) => deleteMcpPlaybook(clientId, id),
    onSettled: invalidate,
  })
}

export function useMcpCatalog(clientId: string) {
  const userId = useCurrentUserId()
  return useQuery({
    queryKey: queryKeys.mcp.catalog(clientId, userId),
    queryFn: () => fetchMcpCatalog(clientId),
    enabled: Boolean(clientId),
    staleTime: 10 * 60 * 1000,
    retry: noRetryOnClientError,
  })
}

export function useMcpCatalogValues(clientId: string, dimension: string, search: string, enabled: boolean) {
  const userId = useCurrentUserId()
  return useQuery({
    queryKey: queryKeys.mcp.catalogValues(clientId, userId, dimension, search),
    queryFn: () => fetchMcpCatalogValues(clientId, dimension, search),
    enabled: enabled && Boolean(clientId && dimension),
    staleTime: 5 * 60 * 1000,
    retry: noRetryOnClientError,
  })
}

export function useMcpFirmProfile(clientId: string) {
  const userId = useCurrentUserId()
  return useQuery({
    queryKey: queryKeys.mcp.firmProfile(clientId, userId),
    queryFn: () => fetchMcpFirmProfile(clientId),
    enabled: Boolean(clientId),
    retry: noRetryOnClientError,
  })
}

export function useSaveMcpFirmProfileMutation(clientId: string) {
  const userId = useCurrentUserId()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (profile: McpFirmProfile) => saveMcpFirmProfile(clientId, profile),
    onSuccess: (saved) => queryClient.setQueryData(queryKeys.mcp.firmProfile(clientId, userId), saved),
  })
}
